import React, { useState, useEffect, useMemo, useContext } from 'react';
import _ from 'lodash';
import {
  Button,
  Grid,
  Typography,
  IconButton,
  Tooltip,
  Checkbox,
} from '@material-ui/core';
import {
  Delete as DeleteIcon
} from '@material-ui/icons';
import { Alert, AlertTitle } from '@material-ui/lab';
import { Trans, useTranslation } from 'react-i18next';
import { useTable } from 'react-table';
import { FormField2 as Field } from 'components';
import { toast, twoOrMoreDecimals } from 'utils/helpers';
import api from 'utils/api';
import { EstimateContext } from './EstimateObjectForm';
import EditableCell from './EditableCell';

const FILTER = {
  OBJECT: 'object',
  ESTIMATE: 'estimate',
  PART: 'part',
  CHAPTER: 'chapter',
  SUBCHAPTER: 'subchapter',
}

const ResourceManager = ({ resourceType, onReload }) => {
  const { t } = useTranslation();
  const { record, loading, setLoading, setShowObjectSummaryModal } = useContext(EstimateContext);
  const [resources, setResources] = useState([]);
  const [resourceGroups, setResourceGroups] = useState([]);
  const [touched, setTouched] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [priceCoefficient, setPriceCoefficient] = useState(record.resourcePriceCoefficients[resourceType]);
  const [filter, setFilter] = useState(FILTER.OBJECT);
  const [selectedResources, setSelectedResources] = useState([]);

  const renderNumberCell = ({ value }) => <div style={{ textAlign: 'right' }}>{twoOrMoreDecimals(value)}</div>;

  // Note: table column width defined in CSS
  const columns = useMemo(() => [
    { Header: '', accessor: 'deleteCheckbox', width: 25, style: { border: "none" } },
    { Header: t('Kodas'), accessor: 'code' },
    { Header: t('Pavadinimas'), accessor: 'title', width: 400, Cell: EditableCell },
    { Header: t('Mat. vnt.'), accessor: 'unit', Cell: EditableCell },
    { Header: t('Kiekis'), accessor: 'quantity', Cell: renderNumberCell },
    { Header: t('Kaina, EUR'), accessor: 'price', Cell: EditableCell },
    { Header: t('Suma, EUR'), accessor: 'amount', Cell: renderNumberCell },
    { Header: '', accessor: 'deleteButton', width: 25, style: { border: "none" } },
  ], []);

  const resetDefaultPrices = async () => {
    setLoading(true);

    try {
      const payload = {
        filter,
        version: record.version.code,
      };

      await api.put(`/estimates/${record._id}/resources/${resourceType}/defaults`, payload);
    } catch (err) {
      console.error(err);
    }

    setLoading(false);
  };

  const fetchResources = async () => {
    setLoading(true);

    try {
      const { data } = await api.get(`/estimates/${record._id}/resources/${resourceType}?filter=${filter}`);

      setResources(data);
      setUpdates([]);
      setTouched(false);
    } catch (err) {
      console.error(err);
    }

    setLoading(false);
  };

  const fetchResourceGroups = async () => {
    setLoading(true);

    try {
      const { data } = await api.get(`/estimates/resource-groups`);

      setResourceGroups(data.sort((a, b) => Number(a.code) - Number(b.code)));
    } catch (err) {
      console.error(err);
    }

    setLoading(false);
  };

  useEffect(() => {
    if (!resourceGroups.length) fetchResourceGroups().then(() => fetchResources());
    if (resourceGroups.length) fetchResources();
  }, [filter]);

  const onCellChange = ({ resource, name, value }) => {
    setTouched(true)

    resource[name] = value;
    setResources([...resources]);

    const newUpdates = [...updates];
    let existing = newUpdates.find(r => {
      if (resource.estimateId && resource.chapterId) {
        return r.code === resource.code
          && r.estimateId === resource.estimateId
          && r.chapterId === resource.chapterId;
      }

      if (resource.estimateId) {
        return r.code === resource.code
          && r.estimateId === resource.estimateId
      }

      return r.code === resource.code;
    });

    if (existing) {
      existing[name] = value;
    } else {
      newUpdates.push({ ...resource });
    }

    setUpdates(newUpdates);
  };

  const handleResourceDelete = async (resourceCode, groupId) => {
    if (!window.confirm(t('Ar tikrai ištrinti resursus?'))) return;

    try {
      await api.delete(`/estimates/${record._id}/resources/${resourceType}`, { data: { groupId, resourceCode } });

      // Refresh data
      await fetchResources();
      onReload(record._id);
    } catch (err) {
      toast(t('Klaida trinant resursus'), 'error');
    }
  }

  const handleResourceSelect = (resourceCode, groupId) => {
    const exists = selectedResources.find(r => r.code === resourceCode && r.groupId === groupId);
    let newSelected = [...selectedResources];

    if (exists) {
      newSelected = newSelected.filter(r => !(r.code === resourceCode && r.groupId === groupId));
    } else {
      newSelected.push({ code: resourceCode, groupId });
    }

    setSelectedResources(newSelected);
  }

  const onDeleteSelectedResources = async () => {
    if (!selectedResources.length) {
      toast(t('Nėra pažymėtų resursų'));
      return;
    }
    if (!window.confirm(t('Ar tikrai ištrinti pažymėtus resursus?'))) return;

    try {
      await api.delete(`/estimates/${record._id}/resources/${resourceType}/group`, { data: { selectedResources } });
      
      // Refresh data
      await fetchResources();
      onReload(record._id);
      toast(t('Pažymėti resursai ištrinti'), 'success');
      setSelectedResources([]);
    } catch (err) {
      toast(t('Klaida trinant resursus'), 'error');
      console.error(err);
    }
  }

  const onPriceCoefficientChange = ({ value }) => {
    setPriceCoefficient(value);
  }

  const onSubmit = async () => {
    setLoading(true);

    try {
      // Update and recalc resources and whole object
      await api.put(`/estimates/${record._id}/resources/${resourceType}/batch`, { updates, filter });

      // Refresh data
      await fetchResources();

      setTouched(false);
      setUpdates([]);
      onReload(record._id);
      toast(t('Perskaičiuota'), 'success');
    } catch (err) {
      toast(t('Klaida apdorojant'), 'error');
      console.error(err);
    }

    setLoading(false);
  }

  const onSubmitPriceCoefficient = async () => {
    if (!window.confirm(t('Ar tikrai perskaičiuoti kainas su koeficientu?'))) return;

    setLoading(true);

    try {
      // Update and recalc resources and whole object
      await api.put(`/estimates/${record._id}/resources/${resourceType}/coefficient`, { priceCoefficient });

      // Refresh data
      await fetchResources();

      setTouched(false);
      setUpdates([]);
      onReload(record._id);
      toast(t('Perskaičiuota'), 'success');
    } catch (err) {
      toast(t('Klaida apdorojant'), 'error');
      console.error(err);
    }

    setLoading(false);
  };

  const onReset = async () => {
    await fetchResources();

    setTouched(false);
    setUpdates([]);
  };

  const onResetDefaults = async () => {
    await resetDefaultPrices();
    await fetchResources();

    setTouched(false);
    setUpdates([]);
    onReload(record._id);
    toast(t('Įkeltos kainos iš kainyno'), 'success');
  }

  const onFilterChange = async ({ value }) => {
    setSelectedResources([]);
    setFilter(value);
  }

  const renderGroupedResourceRows = ({ groupResources, resourceGroup }) => {
    const rows = [];
    if (!groupResources?.length) return rows;

    rows.push(
      <tr key={`${resourceGroup.code}-${groupResources[0]._id}`} className={'group-row'}>
        <td style={{ border: 'none', backgroundColor: 'white' }} />
        <td colSpan={6}>
          {resourceGroup.code}. {resourceGroup.title}
        </td>
      </tr>
    )

    for (const resource of groupResources) {
      const dirty = !!updates.find(u => u._id === resource._id);

      rows.push(
        <tr key={resource._id} className={`item-row ${dirty ? 'dirty' : ''}`}>
          <td align="center" style={{ border: 'none' }}>
            <Checkbox
              color="secondary"
              size="small"
              checked={!!selectedResources.find(r => r.code === resource.code && r.groupId === (resource?.chapterId?.includes('virtual') ? resource.parentOfRate : resource.chapterId || resource.estimateId))}
              onChange={() => handleResourceSelect(
                resource.code, 
                resource?.chapterId?.includes('virtual') ? resource.parentOfRate : resource.chapterId || resource.estimateId
              )}
              disabled={!!record?.isReadonly}
              style={{ marginLeft: -12 }}
            />
          </td>
          <td>
            {resource.code}
          </td>
          <td>
            {!!record?.isReadonly ? resource.title : (
              <Field
                name="title"
                onChange={({ name, value }) => onCellChange({ resource, name, value })}
                value={resource.title}
                className="field"
                naked
              />
            )}
          </td>
          <td align="center">
            {!!record?.isReadonly ? resource.unit : (
              <Field
                name="unit"
                onChange={({ name, value }) => onCellChange({ resource, name, value })}
                value={resource.unit}
                className="field"
                naked
              />
            )}
          </td>
          <td align="right">
            {twoOrMoreDecimals(resource.quantity)}
          </td>
          <td>
            {!!record?.isReadonly ? (
              <div style={{ textAlign: 'right' }}>{twoOrMoreDecimals(resource.price)}</div>
            ) : (
              <Field
                name="price"
                type="number"
                onChange={({ name, value }) => onCellChange({ resource, name, value })}
                value={resource.price}
                className="field field-price"
                naked
              />
            )}
          </td>
          <td align="right">
            {twoOrMoreDecimals(resource.amount)}
          </td>
          <td align="center" style={{ border: 'none' }}>
            <Tooltip title={t('Ištrinti resursus')}>
              <IconButton
                color="secondary"
                size="small"
                onClick={() => handleResourceDelete(
                  resource.code, 
                  resource?.chapterId?.includes('virtual') ? resource.parentOfRate : resource.chapterId || resource.estimateId,
                )}
                disabled={!!record?.isReadonly}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </td>
        </tr>
      )
    }

    return rows;
  }

  const renderResourceLevelRows = (item) => {
    const rows = [];
    if (!item.resources?.length) return rows;

    const ungroupedResources = item.resources.filter(r => !r.resourceGroupCode);
    if (ungroupedResources.length) {
      rows.push(...renderGroupedResourceRows({ resourceGroup: { code: '0', title: t('NESUGRUPUOTA') }, groupResources: ungroupedResources }));
    }

    for (const resourceGroup of resourceGroups) {
      const groupResources = item.resources.filter(r => r.resourceGroupCode === resourceGroup.code);
      if (!groupResources.length) continue;

      rows.push(...renderGroupedResourceRows({ resourceGroup, groupResources }));
    }

    // Totals
    rows.push(
      <tr key={`${item.code}-${item._id}`} className={`totals-${filter} ${touched ? 'touched' : ''}`}>
        <td style={{ border: 'none'}} />
        <td colSpan={5} />
        {/* <td className="total" align="right">
          {twoOrMoreDecimals(item.totals.quantity)}
        </td> */}
        {/* <td /> */}
        <td className="total" align="right">
          {twoOrMoreDecimals(item.totals.amount)}
        </td>
      </tr>
    )

    return rows;
  }

  const renderItemRow = (estimate) => {
    const rows = [];

    rows.push(
      <tr key={`estimate-${estimate._id}`} className={`item-row type-estimate ${touched ? 'touched' : ''}`}>
        <td style={{ background: "none", border: "none" }} />
        <td>{estimate.code}</td>
        <td>{estimate.title}</td>
        <td />
        <td className="total" align="right">
          {twoOrMoreDecimals(estimate.totals.quantity)}
        </td>
        <td />
        <td className="total" align="right">
          {twoOrMoreDecimals(estimate.totals.amount)}
        </td>
      </tr>
    )

    if (estimate?.parts?.length) {
      for (const part of estimate.parts) {

        rows.push(
          <tr key={`estimate-${estimate._id}-part-${part._id}`} className={`item-row type-part ${touched ? 'touched' : ''}`}>
            <td style={{ background: "none", border: "none" }} />
            <td>{part.code}</td>
            <td>{part.title}</td>
            <td />
            <td className="total" align="right">
              {twoOrMoreDecimals(part.totals.quantity)}
            </td>
            <td />
            <td className="total" align="right">
              {twoOrMoreDecimals(part.totals.amount)}
            </td>
          </tr>
        )

        if (part?.chapters?.length) {
          for (const chapter of part.chapters) {
            rows.push(
              <tr key={`estimate-${estimate._id}-part-${part._id}-chapter-${chapter._id}`} className={`item-row type-chapter ${touched ? 'touched' : ''}`}>
                <td style={{ background: "none", border: "none" }} />
                <td>{chapter.code}</td>
                <td>{chapter.title}</td>
                <td />
                <td className="total" align="right">
                  {twoOrMoreDecimals(chapter.totals.quantity)}
                </td>
                <td />
                <td className="total" align="right">
                  {twoOrMoreDecimals(chapter.totals.amount)}
                </td>
              </tr>
            )

            if (chapter?.subchapters?.length) {
              for (const subchapter of chapter.subchapters) {
                rows.push(
                  <tr key={`estimate-${estimate._id}-part-${part._id}-chapter-${chapter._id}-subchapter-${subchapter._id}`} className={`item-row type-subchapter ${touched ? 'touched' : ''}`}>
                    <td style={{ background: "none", border: "none" }} />
                    <td>{subchapter.code}</td>
                    <td>{subchapter.title}</td>
                    <td />
                    <td className="total" align="right">
                      {twoOrMoreDecimals(subchapter.totals.quantity)}
                    </td>
                    <td />
                    <td className="total" align="right">
                      {twoOrMoreDecimals(subchapter.totals.amount)}
                    </td>
                  </tr>
                )

                rows.push(...renderResourceLevelRows(subchapter));
              }
            } else {
              rows.push(...renderResourceLevelRows(chapter));
            }
          }
        } else {
          rows.push(...renderResourceLevelRows(part));
        }
      }
    } else {
      rows.push(...renderResourceLevelRows(estimate));
    }

    // if (chapters) {
    //   for (const chapter of chapters) {
    //     rows.push(
    //       <tr key={`chapter-${chapter._id}`} className={`item-row type-chapter ${touched ? 'touched' : ''}`}>
    //         <td>{chapter.code}</td>
    //         <td>{chapter.title}</td>
    //         <td />
    //         <td className="total" align="right">
    //           {twoOrMoreDecimals(chapter.totals.quantity)}
    //         </td>
    //         <td />
    //         <td className="total" align="right">
    //           {twoOrMoreDecimals(chapter.totals.amount)}
    //         </td>
    //       </tr>
    //     )

    //     rows.push(...renderResourceLevelRows(chapter));
    //   }
    // } else {
    //   rows.push(...renderResourceLevelRows(estimate));
    // }

    return rows;
  }

  const renderRowsByType = (item) => {
    const rows = [];

    if (filter === FILTER.OBJECT) {
      // Filter: object

      rows.push(...renderResourceLevelRows(item));
    } else {
      rows.push(...renderItemRow(item));
    }
    // } else if (filter === FILTER.ESTIMATE) {
    //   // Filter: estimate

    //   rows.push(...renderItemRow(item));
    // } else if (item.chapters) {
    //   // Filter: chapter

    //   rows.push(...renderItemRow(item, item.chapters));
    // }

    return rows;
  }

  // !!! might prevent rerendering
  // const data = useMemo(() => resources, [resou]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
  } = useTable({
    columns,
    data: resources,
  });

  return (
    <div>
      <Grid container justifyContent="space-between" spacing={6} xs={12}>
        <Grid item style={{ padding: '24px 6px 24px 24px' }}>
          <div style={{ display: 'inline-flex' }}>
            <Field
              label={t('Detalizavimas')}
              name="filter"
              value={filter}
              type="select"
              options={[
                { value: FILTER.OBJECT, label: t('Objektas') },
                { value: FILTER.ESTIMATE, label: t('Sąmata') },
                { value: FILTER.PART, label: t('Sąmatos dalis') },
                { value: FILTER.CHAPTER, label: t('Skyrius') },
                { value: FILTER.SUBCHAPTER, label: t('Poskyris') },
              ]}
              style={{ width: 140 }}
              onChange={onFilterChange}
            />

            {!record?.isReadonly && (
              <>
                <Field
                  name="priceCoefficient"
                  onChange={onPriceCoefficientChange}
                  value={priceCoefficient}
                  label={t('Kainos koeficientas')}
                  style={{ width: 130, marginTop: 8, paddingLeft: 16 }}
                  shrink
                />

                <Button
                  color="primary"
                  variant="contained"
                  onClick={onSubmitPriceCoefficient}
                  size="small"
                  style={{ height: 28, marginTop: 10, marginLeft: 16 }}
                >
                  {t('Pritaikyti')}
                </Button>
              </>
            )}
          </div>
        </Grid>

        <Grid item style={{ paddingRight: 0, marginRight: -24 }}>
          <div style={{ margin: '8px 0px', display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ width: 130 }}>
              <Typography style={{ fontWeight: 600 }}>{t('Suma, EUR be PVM')}</Typography>
              <Typography>{record.totals.amount}</Typography>
            </div>
            <div style={{ width: 130, paddingLeft: 10 }}>
              <Typography style={{ fontWeight: 600 }}>{t('Suma, EUR su PVM')}</Typography>
              <Typography>{record.totals.grandTotal}</Typography>
            </div>
            <div style={{ paddingLeft: 10 }}>
              <Button
                variant="outlined"
                color="primary"
                style={{ fontSize: '10px', marginTop: '5px' }}
                onClick={() => setShowObjectSummaryModal(true)}>
                {t('Kainos sudėtis')}
              </Button>
            </div>
          </div>
        </Grid>
      </Grid>

      <div className="resources-table-wrapper">
        <table {...getTableProps()} className="resources-table">
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th
                    width={column.width}
                    style={column.style}
                    {...column.getHeaderProps({ className: `column-type-${column.id}` })}
                  >
                    {column.render('Header')}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {resources.map(resource => renderRowsByType(resource))}
          </tbody>
        </table>
      </div>

      {touched && (
        <div style={{ marginTop: 20 }}>
          <Alert severity="warning">
            <AlertTitle>{t('Dėmesio')}</AlertTitle>
            {filter === 'object'
              ? <Trans t={t}>Resursų pakeitimai bus pritaikyti visam <b>objektui</b></Trans>
              : (
                <Trans t={t}>Resursų pakeitimai bus pritaikyti visam objektui <b>{`${filter === 'estimate' ? 'sąmatos' : (filter === 'chapter' ? 'skyriaus' : '-')}`}</b> lygyje</Trans>
              )
            }
          </Alert>
        </div>
      )}

      {!record?.isReadonly && (
        <div style={{ marginTop: 20, textAlign: "right", position: "sticky", width: "100%", bottom: 0, height: 55, backgroundColor: "#fff", paddingTop: 10, paddingBottom: 10, borderTop: "1px solid #ddd" }}>
          <Button
            color="primary"
            variant="contained"
            onClick={onDeleteSelectedResources}
            size="small"
            disabled={selectedResources.length === 0 || loading}
          >
            {t('Ištrinti pažymėtus resursus')}
          </Button>

          <Button
            color="primary"
            variant="contained"
            onClick={onResetDefaults}
            size="small"
            disabled={loading}
            style={{ marginLeft: 10 }}
          >
            {t('Atstatyti kainas iš kainyno')}
          </Button>

          <Button
            color="primary"
            variant="contained"
            onClick={onSubmit}
            disabled={!touched || loading}
            size="small"
            style={{ marginLeft: 10 }}
          >
            {t('Perskaičiuoti resursus')}
          </Button>

          <Button
            color="primary"
            variant="outlined"
            onClick={onReset}
            disabled={!touched || loading}
            style={{ marginLeft: 10 }}
          >
            {t('Atšaukti')}
          </Button>
        </div>
      )}
    </div>
  );
}

export default ResourceManager;